import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import RevenueReports from '../../components/reports/RevenueReports';
import OccupancyReports from '../../components/reports/OccupancyReports';
import PropertyPerformance from '../../components/reports/PropertyPerformance';
import TourPerformance from '../../components/reports/TourPerformance';
import {
  adminReportsMeta,
  adminRevenue,
  adminOccupancy,
  adminPropertyPerformance,
  adminTourPerformance,
  adminExportReport,
  adminTopHosts,
  adminHostReports,
  adminClientReports,
} from '../../api/reports';
import { useToast } from '../../components/ToastContext';
import AdminHostReports from '../../components/reports/AdminHostReports';
import AdminClientReports from '../../components/reports/AdminClientReports';

const tabs = [
  { key: 'revenue', label: 'Revenue reports' },
  { key: 'occupancy', label: 'Occupancy reports' },
  { key: 'property', label: 'Property performance' },
  { key: 'tour', label: 'Tour performance' },
  { key: 'hosts', label: 'Host reports' },
  { key: 'clients', label: 'Client reports' },
];

const toLocalIsoDate = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const todayIso = () => toLocalIsoDate(new Date());
const daysAgoIso = (days) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return toLocalIsoDate(d);
};

const buildDefaultFilters = (hostId = '') => {
  const range = { from: daysAgoIso(30), to: todayIso() };
  return {
    revenue: { ...range, groupBy: 'day', type: 'all', hostId, propertyId: '', tourId: '', clientId: '' },
    occupancy: { ...range, groupBy: 'day', hostId, propertyId: '' },
    property: { ...range, hostId, propertyId: '' },
    tour: { ...range, hostId, tourId: '' },
    hosts: { ...range, hostId },
    clients: { ...range, hostId, clientId: '' },
  };
};

const AdminReports = ({ token }) => {
  const toast = useToast();
  const { tab: tabParam } = useParams();
  const [activeTab, setActiveTab] = useState('revenue');
  const [filters, setFilters] = useState(buildDefaultFilters);
  const [selectedHost, setSelectedHost] = useState('');
  const [meta, setMeta] = useState({ hosts: [], clients: [], properties: [], tours: [] });
  const [loadingMeta, setLoadingMeta] = useState(false);
  const [loading, setLoading] = useState({
    revenue: false,
    occupancy: false,
    property: false,
    tour: false,
    hosts: false,
    clients: false,
  });
  const [exporting, setExporting] = useState(false);
  const [data, setData] = useState({ revenue: null, occupancy: null, property: [], tour: [], hosts: [], clients: [] });
  const [topHosts, setTopHosts] = useState([]);
  const [error, setError] = useState('');
  const requestedTab = (tabParam || '').toLowerCase();
  const normalizedTab = tabs.some((t) => t.key === requestedTab) ? requestedTab : '';

  const loadHosts = async () => {
    if (!token) return;
    setLoadingMeta(true);
    try {
      const res = await adminReportsMeta(token);
      setMeta((m) => ({
        ...m,
        hosts: res.hosts || [],
        clients: res.clients || [],
        properties: res.properties || [],
        tours: res.tours || [],
      }));
    } catch (err) {
      toast.error(err.message || 'Failed to load hosts');
    } finally {
      setLoadingMeta(false);
    }
  };

  const loadHostResources = async (hostId) => {
    if (!token || !hostId) {
      setMeta((m) => ({ ...m, properties: [], tours: [] }));
      return;
    }
    setLoadingMeta(true);
    try {
      const res = await adminReportsMeta(token, hostId);
      setMeta((m) => ({
        ...m,
        properties: res.properties || [],
        tours: res.tours || [],
      }));
    } catch (err) {
      toast.error(err.message || 'Failed to load host resources');
    } finally {
      setLoadingMeta(false);
    }
  };

  const loadTopHosts = async (range) => {
    if (!token) return;
    try {
      const rows = await adminTopHosts(token, { from: range.from, to: range.to, metric: 'earnings' });
      setTopHosts(rows || []);
    } catch {
      setTopHosts([]);
    }
  };

  const loadTab = async (tab, overrideFilters) => {
    if (!token) return;
    const tabFilters = overrideFilters || filters[tab];
    setError('');
    setLoading((s) => ({ ...s, [tab]: true }));
    try {
      if (tab === 'revenue') {
        const res = await adminRevenue(token, tabFilters);
        setData((d) => ({ ...d, revenue: res }));
        loadTopHosts(tabFilters);
      } else if (tab === 'occupancy') {
        const res = await adminOccupancy(token, tabFilters);
        setData((d) => ({ ...d, occupancy: res }));
      } else if (tab === 'property') {
        const res = await adminPropertyPerformance(token, tabFilters);
        setData((d) => ({ ...d, property: res }));
      } else if (tab === 'tour') {
        const res = await adminTourPerformance(token, tabFilters);
        setData((d) => ({ ...d, tour: res }));
      } else if (tab === 'hosts') {
        const res = await adminHostReports(token, tabFilters);
        setData((d) => ({ ...d, hosts: res }));
      } else if (tab === 'clients') {
        const res = await adminClientReports(token, tabFilters);
        setData((d) => ({ ...d, clients: res }));
      }
    } catch (err) {
      setError(err.message || 'Failed to load report');
    } finally {
      setLoading((s) => ({ ...s, [tab]: false }));
    }
  };

  useEffect(() => {
    loadHosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (normalizedTab && normalizedTab !== activeTab) {
      setActiveTab(normalizedTab);
    }
  }, [normalizedTab, activeTab]);

  useEffect(() => {
    loadHostResources(selectedHost);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedHost]);

  useEffect(() => {
    loadTab(activeTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, token]);

  const handleFiltersChange = (tab) => (next) => {
    const hostChanged = next.hostId !== selectedHost;
    if (hostChanged) {
      const updated = buildDefaultFilters(next.hostId || '');
      setSelectedHost(next.hostId || '');
      setFilters(updated);
      loadTab(activeTab, updated[activeTab]);
      return;
    }
    setFilters((f) => ({ ...f, [tab]: next }));
  };

  const handleReset = (tab) => {
    const updated = buildDefaultFilters(selectedHost);
    setFilters(updated);
    loadTab(tab, updated[tab]);
  };

  const handleApply = (tab, nextFilters) => {
    setFilters((f) => ({ ...f, [tab]: nextFilters }));
    loadTab(tab, nextFilters);
  };

  const handleExport = async (tab, tabFilters) => {
    setExporting(true);
    try {
      const typeMap = {
        revenue: 'revenue',
        occupancy: 'occupancy',
        property: 'property',
        tour: 'tour',
        hosts: 'hosts',
        clients: 'clients',
      };
      const csv = await adminExportReport(token, { ...tabFilters, type: typeMap[tab] });
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${typeMap[tab] || 'report'}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Export ready');
    } catch (err) {
      toast.error(err.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const renderTab = () => {
    if (activeTab === 'revenue') {
      return (
        <div className="space-y-4">
          <RevenueReports
            data={data.revenue}
            filters={filters.revenue}
            onFiltersChange={handleFiltersChange('revenue')}
            onApply={(next) => handleApply('revenue', next)}
            onReset={() => handleReset('revenue')}
            onExport={(next) => handleExport('revenue', next)}
            properties={meta.properties}
            tours={meta.tours}
            hosts={meta.hosts}
            clients={meta.clients}
            showHost
            showClient
            loading={loading.revenue}
            exporting={exporting}
            loadingMeta={loadingMeta}
          />
          {topHosts.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-slate-800">Top hosts</h3>
              <ul className="admin-card divide-y divide-slate-200 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                {topHosts.map((h) => (
                  <li key={h.hostId} className="flex items-center justify-between px-4 py-3 text-sm text-slate-800">
                    <div>
                      <p className="font-semibold text-slate-900">{h.hostName}</p>
                      <p className="text-xs text-slate-500">
                        {h.bookings} bookings / {Number(h.revenue || 0).toLocaleString('en-IN')} revenue
                      </p>
                    </div>
                    <span className="text-sm font-semibold text-emerald-700">
                      INR {Number(h.earnings || 0).toLocaleString('en-IN')}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      );
    }
    if (activeTab === 'occupancy') {
      return (
        <OccupancyReports
          data={data.occupancy}
          filters={filters.occupancy}
          onFiltersChange={handleFiltersChange('occupancy')}
          onApply={(next) => handleApply('occupancy', next)}
          onReset={() => handleReset('occupancy')}
          onExport={(next) => handleExport('occupancy', next)}
          properties={meta.properties}
          hosts={meta.hosts}
          showHost
          loading={loading.occupancy}
          exporting={exporting}
          loadingMeta={loadingMeta}
        />
      );
    }
    if (activeTab === 'property') {
      return (
        <PropertyPerformance
          data={data.property}
          filters={filters.property}
          onFiltersChange={handleFiltersChange('property')}
          onApply={(next) => handleApply('property', next)}
          onReset={() => handleReset('property')}
          onExport={(next) => handleExport('property', next)}
          properties={meta.properties}
          hosts={meta.hosts}
          showHost
          loading={loading.property}
          exporting={exporting}
          loadingMeta={loadingMeta}
        />
      );
    }
    if (activeTab === 'tour') {
      return (
        <TourPerformance
          data={data.tour}
          filters={filters.tour}
          onFiltersChange={handleFiltersChange('tour')}
          onApply={(next) => handleApply('tour', next)}
          onReset={() => handleReset('tour')}
          onExport={(next) => handleExport('tour', next)}
          tours={meta.tours}
          hosts={meta.hosts}
          showHost
          loading={loading.tour}
          exporting={exporting}
          loadingMeta={loadingMeta}
        />
      );
    }
    if (activeTab === 'hosts') {
      return (
        <AdminHostReports
          data={data.hosts}
          filters={filters.hosts}
          onFiltersChange={handleFiltersChange('hosts')}
          onApply={(next) => handleApply('hosts', next)}
          onReset={() => handleReset('hosts')}
          onExport={(next) => handleExport('hosts', next)}
          hosts={meta.hosts}
          loading={loading.hosts}
          exporting={exporting}
        />
      );
    }
    if (activeTab === 'clients') {
      return (
        <AdminClientReports
          data={data.clients}
          filters={filters.clients}
          onFiltersChange={handleFiltersChange('clients')}
          onApply={(next) => handleApply('clients', next)}
          onReset={() => handleReset('clients')}
          onExport={(next) => handleExport('clients', next)}
          hosts={meta.hosts}
          clients={meta.clients}
          loading={loading.clients}
          exporting={exporting}
        />
      );
    }
    return null;
  };

  return (
    <div className="space-y-4">
      <div className="admin-card rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Homevilas</p>
            <h1 className="admin-page-title text-xl font-bold text-slate-900">Admin Reports</h1>
            <p className="text-sm text-slate-600">Platform and per-host analytics.</p>
          </div>
        </div>
      </div>
      <div className="grid gap-4 lg:grid-cols-[240px,1fr]">
        <aside className="rounded-2xl border border-slate-200 bg-slate-900 text-white shadow-sm">
          <div className="border-b border-slate-800 px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-300">
            Reports
          </div>
          <div className="p-3 space-y-2">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-sm font-semibold transition ${
                  activeTab === tab.key ? 'bg-white text-slate-900 shadow-sm' : 'bg-slate-800 text-slate-200 hover:bg-slate-700'
                }`}
              >
                <span>{tab.label}</span>
                <span className="h-2 w-2 rounded-full bg-current" />
              </button>
            ))}
          </div>
        </aside>

        <div className="space-y-4">
          {loadingMeta && (
            <div className="rounded-xl border border-slate-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              Loading report data...
            </div>
          )}
          {error && (
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              {error}
            </div>
          )}
          {renderTab()}
        </div>
      </div>
    </div>
  );
};

export default AdminReports;
